<template>
  <div class="purchase-recap">
    <div class="purchase-recap__row">
      <span>{{ t('billing.purchases.recap.reference') }}</span>
      <code class="purchase-recap__uid">{{ purchase.uid }}</code>
    </div>
    <div class="purchase-recap__row">
      <span>{{ t('billing.purchases.recap.status') }}</span>
      <v-chip :color="purchaseColor(shownStatus)" size="small" variant="tonal">
        {{ t(`billing.purchases.status.${shownStatus}`) }}
      </v-chip>
    </div>
    <div class="purchase-recap__row">
      <span>{{ t('billing.purchases.recap.credited') }}</span>
      <span>{{ priceExclTax(purchase.credits_cents + purchase.bonus_credits_cents, purchase.currency) }}</span>
    </div>
    <!-- What the payment step owes the payer (U2): the amount excluding tax, the tax and the total
         including it, all three the invoicing service's figures — nothing is computed here. -->
    <template v-if="chargedCents !== null">
      <div class="purchase-recap__row">
        <span>{{ t('billing.purchases.recap.amountExclTax') }}</span>
        <span>{{ price(purchase.amount_excl_tax_cents, purchase.currency) }}</span>
      </div>
      <div v-if="taxCents !== null" class="purchase-recap__row">
        <span>{{ t('billing.purchases.recap.tax') }}</span>
        <span>{{ price(taxCents, purchase.currency) }}</span>
      </div>
      <div class="purchase-recap__row purchase-recap__row--total">
        <span>{{ t('billing.purchases.recap.charged') }}</span>
        <span>{{ price(chargedCents, purchase.currency) }}</span>
      </div>
    </template>
    <!-- A sale this application opened and never got to the invoicing service. It closes on its
         own deadline and nothing was charged — which is worth saying, since the row exists. -->
    <div v-if="!purchase.opened_at_ledger" class="purchase-recap__note">
      {{ t('billing.purchases.recap.neverOpened') }}
    </div>
    <div v-if="needsConfirmation(purchase)" class="purchase-recap__note">
      {{ t('billing.purchases.recap.confirmationNote') }}
    </div>
    <div v-else-if="purchase.payment_failure_reason" class="purchase-recap__note">
      {{ t('billing.purchases.recap.lastFailure', {
              reason: t(paymentFailureReasonKey(purchase.payment_failure_reason)),
            }) }}
    </div>
  </div>
</template>

<script setup lang="ts">
/** One top-up's facts, as the modal and the history both need them stated. */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMoney } from '../composables/useMoney'
import type { CreditPurchaseView } from '@/api/billing/purchases'
import {
  displayedPurchaseStatus,
  needsConfirmation,
  paymentFailureReasonKey,
  purchaseColor,
} from '../status'

const props = defineProps<{ purchase: CreditPurchaseView }>()

const { t } = useI18n()
const { price, priceExclTax } = useMoney()

const shownStatus = computed(() => displayedPurchaseStatus(props.purchase))

/** `null` on a row whose sale never reached the invoicing service: there is no total to state. */
const chargedCents = computed(() => props.purchase.total_incl_tax_cents ?? null)
const taxCents = computed(() => props.purchase.total_tax_cents ?? null)
</script>

<style scoped lang="scss">
@use '@/styles/design-tokens' as *;

.purchase-recap {
  padding: $space-md $space-lg;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: $radius-lg;

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $space-md;
    padding: 3px 0;
    font-size: $text-md;

    &--total {
      font-weight: $font-semibold;
    }
  }

  &__uid {
    font-size: $text-xs;
    font-family: monospace;
    opacity: 0.8;
  }

  &__note {
    margin-top: $space-sm;
    font-size: $text-xs;
    color: rgba(var(--v-theme-on-surface), $opacity-tertiary);
  }
}
</style>
